// src/services/syncQueueService.ts - 同步佇列管理服務
import { Capture } from '../types/capture';
import { TimeBox } from '../types/timeBox';
import { createCapture, updateCapture, deleteCapture } from './api/captures';
import { createTimeBox, updateTimeBox, deleteTimeBox } from './api/timeBoxes';
import { initDB, syncQueueStore, replaceTimeBoxTempId } from './indexedDB';
import { logInfo, logWarn, logError, logDev } from '../utils/logger';


// 同步操作類型
export type SyncOperation =
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'CREATE_TIMEBOX'   // TimeBox 特定操作
  | 'UPDATE_TIMEBOX'
  | 'DELETE_TIMEBOX';

// 同步佇列項目介面
export interface SyncQueueItem {
  id: string; // 佇列項目 ID
  operation: SyncOperation;
  captureId?: string; // Capture ID（Capture 專用）
  timeBoxId?: string; // TimeBox ID（TimeBox 專用）
  data?: Partial<Capture | TimeBox>; // 操作數據
  timestamp: number; // 操作時間戳
  retryCount: number; // 重試次數
  maxRetries: number; // 最大重試次數
}


/**
 * 添加操作到同步佇列
 * @param operation 操作類型
 * @param itemId Capture/TimeBox ID
 * @param data 操作數據
 */
export const addToSyncQueue = async (
  operation: SyncOperation,
  itemId: string,
  data?: Partial<Capture | TimeBox>
): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(syncQueueStore, 'readwrite');
    const store = tx.objectStore(syncQueueStore);

    // 判斷是 Capture 還是 TimeBox 操作
    const isCaptureOp = ['CREATE', 'UPDATE', 'DELETE'].includes(operation);

    const queueItem: SyncQueueItem = {
      id: `${operation}_${itemId}_${Date.now()}`,
      operation,
      ...(isCaptureOp ? { captureId: itemId } : { timeBoxId: itemId }),
      data,
      timestamp: Date.now(),
      retryCount: 0,
      maxRetries: 3
    };

    return new Promise((resolve, reject) => {
      const request = store.put(queueItem);

      request.onsuccess = () => {
        logInfo('已添加到同步佇列', { operation, itemId });
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    logError('Failed to add to sync queue', error);
    throw error;
  }
};

/**
 * 獲取所有待同步的佇列項目
 */
export const getSyncQueue = async (): Promise<SyncQueueItem[]> => {
  try {
    const db = await initDB();
    const tx = db.transaction(syncQueueStore, 'readonly');
    const store = tx.objectStore(syncQueueStore);
    const index = store.index('timestamp');

    return new Promise((resolve, reject) => {
      const request = index.getAll();

      request.onsuccess = () => {
        const items = request.result.sort((a, b) => a.timestamp - b.timestamp);
        resolve(items);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    logError('Failed to get sync queue', error);
    throw error;
  }
};

/**
 * 從同步佇列移除項目
 * @param queueItemId 佇列項目 ID
 */
export const removeFromSyncQueue = async (queueItemId: string): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(syncQueueStore, 'readwrite');
    const store = tx.objectStore(syncQueueStore);

    return new Promise((resolve, reject) => {
      const request = store.delete(queueItemId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    logError('Failed to remove from sync queue', error);
    throw error;
  }
};

/**
 * 更新佇列項目的重試次數
 * @param queueItemId 佇列項目 ID
 */
export const incrementRetryCount = async (queueItemId: string): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(syncQueueStore, 'readwrite');
    const store = tx.objectStore(syncQueueStore);

    return new Promise((resolve, reject) => {
      const getRequest = store.get(queueItemId);

      getRequest.onsuccess = () => {
        if (getRequest.result) {
          const item = getRequest.result;
          item.retryCount += 1;

          const putRequest = store.put(item);

          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve(); // 項目已不存在
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  } catch (error) {
    logError('Failed to increment retry count', error);
    throw error;
  }
};

/**
 * 通過目標 ID 移除同步佇列項目 (用於取消待處理的操作)
 * @param operation 操作類型
 * @param targetId 目標 ID (captureId 或 timeBoxId)
 */
export const removeSyncQueueItemByTargetId = async (
  operation: SyncOperation,
  targetId: string
): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(syncQueueStore, 'readwrite');
    const store = tx.objectStore(syncQueueStore);

    return new Promise((resolve, reject) => {
      // 使用 cursor 遍歷尋找匹配的項目
      const request = store.openCursor();
      let found = false;
      const deletePromises: Promise<void>[] = [];

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result as IDBCursorWithValue;

        if (cursor) {
          const item = cursor.value as SyncQueueItem;

          // 檢查是否匹配操作類型和目標 ID
          let isMatch = false;
          if (item.operation === operation) {
            if (operation.includes('CAPTURE') && item.captureId === targetId) {
              isMatch = true;
            } else if (operation.includes('TIMEBOX') && item.timeBoxId === targetId) {
              isMatch = true;
            }
          }

          if (isMatch) {
            found = true;
            logInfo('移除 pending 同步任務', { itemId: item.id, operation, targetId });

            // 刪除此項目
            const deleteRequest = cursor.delete();
            const deletePromise = new Promise<void>((res, rej) => {
              deleteRequest.onsuccess = () => res();
              deleteRequest.onerror = () => rej(deleteRequest.error);
            });
            deletePromises.push(deletePromise);
          }

          cursor.continue();
        } else {
          // 遍歷結束
          Promise.all(deletePromises)
            .then(() => resolve())
            .catch(reject);
        }
      };

      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    logError('Failed to remove sync queue item by target ID', { operation, targetId, error });
    throw error;
  }
};

/**
 * 執行單個同步操作
 * @param queueItem 佇列項目
 */
export const executeSyncOperation = async (queueItem: SyncQueueItem): Promise<void> => {
  const { operation, captureId, timeBoxId, data } = queueItem;

  try {
    // Capture 操作
    if (operation === 'CREATE' && captureId) {
      if (data) {
        logInfo('執行同步創建 Capture', { captureId });
        await createCapture({
          content: (data as Partial<Capture>).content || '',
          userId: (data as Partial<Capture>).userId || '',
          timeBoxId: (data as Partial<Capture>).timeBoxId,
          timeBoxTitle: (data as Partial<Capture>).timeBoxTitle,
          relatedItemId: (data as Partial<Capture>).relatedItemId,
          relatedItemType: (data as Partial<Capture>).relatedItemType,
          relatedItemTitle: (data as Partial<Capture>).relatedItemTitle,
        });
      }
    } else if (operation === 'UPDATE' && captureId) {
      if (data) {
        logInfo('執行同步更新 Capture', { captureId });
        await updateCapture(captureId, data as Partial<Capture>);
      }
    } else if (operation === 'DELETE' && captureId) {
      logInfo('執行同步刪除 Capture', { captureId });
      await deleteCapture(captureId);
    }
    // TimeBox 操作
    else if (operation === 'CREATE_TIMEBOX' && timeBoxId && data) {
      logInfo('執行同步創建 TimeBox', { timeBoxId });
      const timeBoxData = data as Partial<TimeBox>;

      // 驗證必要欄位
      if (!timeBoxData.startTime || !timeBoxData.endTime || !timeBoxData.category) {
        throw new Error('缺少必要的 TimeBox 欄位: startTime, endTime, category');
      }

      const createdTimeBox = await createTimeBox({
        userId: timeBoxData.userId || '',
        itemType: 'timebox',
        title: timeBoxData.title || '',
        notes: timeBoxData.notes || timeBoxData.description,
        startTime: timeBoxData.startTime,
        endTime: timeBoxData.endTime,
        category: timeBoxData.category,
        status: timeBoxData.status || 'normal',
        taskId: timeBoxData.taskId,
        location: timeBoxData.location,
      });

      // 替換臨時 ID
      // 支援 temp- (標準) 和 temp_ (可能的舊格式)
      if (timeBoxId.startsWith('temp')) {
        await replaceTimeBoxTempId(timeBoxId, createdTimeBox.id);
      }
    } else if (operation === 'UPDATE_TIMEBOX' && timeBoxId && data) {
      logInfo('執行同步更新 TimeBox', { timeBoxId });
      await updateTimeBox({
        id: timeBoxId,
        ...data,
      });
    } else if (operation === 'DELETE_TIMEBOX' && timeBoxId) {
      logInfo('執行同步刪除 TimeBox', { timeBoxId });
      // 從伺服器刪除
      await deleteTimeBox(timeBoxId);

      // 確保也從 IndexedDB 刪除（防止重新載入時恢復）
      try {
        const { deleteTimeBoxLocal } = await import('./indexedDB');
        await deleteTimeBoxLocal(timeBoxId);
        logInfo('同步刪除後，已確保從 IndexedDB 移除', { timeBoxId });
      } catch (localError) {
        logWarn('從 IndexedDB 刪除失敗，但伺服器已刪除', { timeBoxId, error: localError });
      }
    } else {
      throw new Error(`未知的同步操作: ${operation}`);
    }
  } catch (error) {
    logError('同步操作失敗', { operation, id: captureId || timeBoxId, error });
    throw error;
  }
};

/**
 * 合併同步佇列中對同一 TimeBox 的多個操作
 *
 * 合併規則：
 * - CREATE + UPDATE → 更新 CREATE 的 data
 * - UPDATE + UPDATE → 合併所有更新
 * - CREATE + DELETE → 移除（抵消）
 * - UPDATE + DELETE → 只保留 DELETE
 */
const consolidateSyncQueue = async (): Promise<void> => {
  try {
    const queue = await getSyncQueue();
    const consolidated = new Map<string, SyncQueueItem>();

    // 按時間戳排序處理
    for (const item of queue.sort((a, b) => a.timestamp - b.timestamp)) {
      const key = item.timeBoxId || item.captureId || '';
      if (!key) continue;

      // 只合併 TimeBox 操作，Capture 保持原樣
      if (!item.timeBoxId) {
        // 對於 Capture 操作，直接保留
        const captureKey = `capture_${key}`;
        if (!consolidated.has(captureKey)) {
          consolidated.set(captureKey, item);
        }
        continue;
      }

      if (!consolidated.has(key)) {
        consolidated.set(key, item);
      } else {
        const existing = consolidated.get(key)!;

        if (existing.operation === 'CREATE_TIMEBOX') {
          if (item.operation === 'UPDATE_TIMEBOX') {
            // CREATE + UPDATE → 合併到 CREATE
            existing.data = { ...existing.data, ...item.data };
            existing.timestamp = item.timestamp;
            logDev('[合併佇列] CREATE + UPDATE → 更新 CREATE', { key });
          } else if (item.operation === 'DELETE_TIMEBOX') {
            // CREATE + DELETE → 抵消
            consolidated.delete(key);
            logDev('[合併佇列] CREATE + DELETE → 抵消', { key });
          }
        } else if (existing.operation === 'UPDATE_TIMEBOX') {
          if (item.operation === 'UPDATE_TIMEBOX') {
            // UPDATE + UPDATE → 合併
            existing.data = { ...existing.data, ...item.data };
            existing.timestamp = item.timestamp;
            logDev('[合併佇列] UPDATE + UPDATE → 合併', { key });
          } else if (item.operation === 'DELETE_TIMEBOX') {
            // UPDATE + DELETE → DELETE
            consolidated.set(key, item);
            logDev('[合併佇列] UPDATE + DELETE → 保留 DELETE', { key });
          }
        }
      }
    }

    // 清空佇列並重新寫入合併後的項目
    const db = await initDB();
    const tx = db.transaction([syncQueueStore], 'readwrite');
    const store = tx.objectStore(syncQueueStore);

    return new Promise((resolve, reject) => {
      // 先清空
      const clearRequest = store.clear();

      clearRequest.onsuccess = () => {
        // 重新添加合併後的項目
        let addCount = 0;
        const itemsArray = Array.from(consolidated.values());

        if (itemsArray.length === 0) {
          resolve();
          return;
        }

        for (const item of itemsArray) {
          const addRequest = store.add(item);

          addRequest.onsuccess = () => {
            addCount++;
            if (addCount === itemsArray.length) {
              logInfo('[合併佇列] 完成', { original: queue.length, consolidated: itemsArray.length });
              resolve();
            }
          };

          addRequest.onerror = () => reject(addRequest.error);
        }
      };

      clearRequest.onerror = () => reject(clearRequest.error);
    });
  } catch (error) {
    logError('[合併佇列] 失敗', error);
    throw error;
  }
};

/**
 * 處理整個同步佇列
 * @param onProgress 進度回調函數（可選）
 */
export const processSyncQueue = async (
  onProgress?: (processed: number, total: number) => void
): Promise<{ success: number; failed: number }> => {
  logInfo('開始處理同步佇列');

  let successCount = 0;
  let failedCount = 0;

  try {
    // ✅ 先合併佇列
    await consolidateSyncQueue();

    const queueItems = await getSyncQueue();

    if (queueItems.length === 0) {
      logInfo('同步佇列為空');
      return { success: 0, failed: 0 };
    }

    logInfo('找到待同步項目', { count: queueItems.length });

    for (let i = 0; i < queueItems.length; i++) {
      const item = queueItems[i];

      try {
        await executeSyncOperation(item);
        await removeFromSyncQueue(item.id);
        successCount++;

        logInfo('同步成功', { operation: item.operation, id: item.captureId || item.timeBoxId });
      } catch (error) {
        logError('同步失敗', { operation: item.operation, id: item.captureId || item.timeBoxId, error });

        // 增加重試次數
        await incrementRetryCount(item.id);

        // 檢查是否超過最大重試次數
        if (item.retryCount >= item.maxRetries) {
          logWarn('達到最大重試次數，移除佇列項目', { itemId: item.id });
          await removeFromSyncQueue(item.id);
        }

        failedCount++;
      }

      // 回調進度
      if (onProgress) {
        onProgress(i + 1, queueItems.length);
      }
    }

    logInfo('同步佇列處理完成', { success: successCount, failed: failedCount });
    return { success: successCount, failed: failedCount };

  } catch (error) {
    logError('處理同步佇列失敗', error);
    throw error;
  }
};

/**
 * 清空同步佇列
 * 謹慎使用：這會刪除所有未同步的操作
 */
export const clearSyncQueue = async (): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(syncQueueStore, 'readwrite');
    const store = tx.objectStore(syncQueueStore);

    return new Promise((resolve, reject) => {
      const request = store.clear();

      request.onsuccess = () => {
        logInfo('同步佇列已清空');
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    logError('Failed to clear sync queue', error);
    throw error;
  }
};

/**
 * 獲取同步佇列統計
 */
export const getSyncQueueStats = async (): Promise<{
  total: number;
  byOperation: Record<SyncOperation, number>;
  oldestItem: Date | null;
}> => {
  try {
    const queueItems = await getSyncQueue();

    const stats = {
      total: queueItems.length,
      byOperation: {
        CREATE: 0,
        UPDATE: 0,
        DELETE: 0,
        CREATE_TIMEBOX: 0,
        UPDATE_TIMEBOX: 0,
        DELETE_TIMEBOX: 0
      } as Record<SyncOperation, number>,
      oldestItem: null as Date | null
    };

    // 統計各操作類型數量
    queueItems.forEach(item => {
      stats.byOperation[item.operation]++;
    });

    // 找出最舊的項目
    if (queueItems.length > 0) {
      const oldestTimestamp = Math.min(...queueItems.map(item => item.timestamp));
      stats.oldestItem = new Date(oldestTimestamp);
    }

    return stats;
  } catch (error) {
    logError('Failed to get sync queue stats', error);
    throw error;
  }
};

/**
 * 獲取 TimeBox 同步佇列統計
 */
export const getTimeBoxSyncStats = async (): Promise<{
  pending: number;
  total: number;
}> => {
  try {
    const queueItems = await getSyncQueue();
    const timeBoxItems = queueItems.filter(item =>
      item.operation.includes('TIMEBOX')
    );

    return {
      pending: timeBoxItems.length,
      total: queueItems.length
    };
  } catch (error) {
    logError('獲取 TimeBox 同步統計失敗', error);
    return { pending: 0, total: 0 };
  }
};

/**
 * 更新待處理的創建 TimeBox 任務
 * 用於在離線模式下編輯尚未同步的新建 TimeBox
 * @param timeBoxId TimeBox ID
 * @param data 新的 TimeBox 數據
 */
export const updatePendingCreateTimeBox = async (
  timeBoxId: string,
  data: Partial<TimeBox>
): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(syncQueueStore, 'readwrite');
    const store = tx.objectStore(syncQueueStore);

    // 我們需要找到針對此 timeBoxId 的 CREATE_TIMEBOX 操作
    // 由於沒有直接對 timeBoxId 建立索引，且無法直接通過 ID 查詢（因為 ID 是生成的）
    // 我們可以使用 getAll 來查找（假設隊列不會太長）
    // 或者使用 cursor 遍歷

    return new Promise((resolve, reject) => {
      // 使用 cursor 遍歷尋找
      const request = store.openCursor();
      let found = false;

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result as IDBCursorWithValue;

        if (cursor) {
          const item = cursor.value as SyncQueueItem;

          // 找到對應的 CREATE_TIMEBOX 操作
          if (item.operation === 'CREATE_TIMEBOX' && item.timeBoxId === timeBoxId) {
            found = true;

            // 更新數據
            const updatedItem: SyncQueueItem = {
              ...item,
              data: {
                ...item.data,
                ...data
              },
              timestamp: Date.now() // 更新時間戳
            };

            const updateRequest = cursor.update(updatedItem);

            updateRequest.onsuccess = () => {
              logInfo('已更新同步佇列中的創建任務', { timeBoxId });
              resolve();
            };

            updateRequest.onerror = () => reject(updateRequest.error);
            // 找到後不需要繼續遍歷
            return;
          }

          cursor.continue();
        } else {
          // 遍歷結束仍未找到
          if (!found) {
            logWarn('未在同步佇列中找到 TimeBox 的創建任務，將創建新的更新任務', { timeBoxId });
            // 如果沒找到 CREATE 任務，則回退到標準的 addToSyncQueue
            addToSyncQueue('UPDATE_TIMEBOX', timeBoxId, data)
              .then(() => resolve())
              .catch(reject);
          }
        }
      };

      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    logError('更新待處理 TimeBox 任務失敗', error);
    throw error;
  }
};